import { useEffect, useRef } from "react";
import { useSoundEffects } from "@/hooks/useSoundEffects";
import { SoundEffect } from "@/lib/soundEffects";

/**
 * SoundProvider component adds sound effects globally to common elements
 * This makes it easy to have sound effects throughout the site without modifying every component
 */
const SoundProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { attachSoundToEvent, playSound, debouncedScrollSound } =
    useSoundEffects();

  const cleanupFunctions = useRef<Array<() => void>>([]);

  // Apply sound effects to common UI elements site-wide
  useEffect(() => {
    // Clean up any previous event listeners
    cleanupFunctions.current.forEach((cleanup) => cleanup());
    cleanupFunctions.current = [];

    // Wait a bit to make sure the DOM is ready
    const timeout = setTimeout(() => {
      // Apply hover sound to all buttons that don't already have sounds
      document.querySelectorAll("button:not(.has-sound)").forEach((button) => {
        const cleanup = attachSoundToEvent(
          button as HTMLElement,
          "mouseenter",
          SoundEffect.HOVER,
          {
            debounce: true,
            debounceTime: 50,
          }
        );
        cleanupFunctions.current.push(cleanup);
      });

      // Apply hover sound to all menu items and links that don't have sounds
      document
        .querySelectorAll(
          'a:not([data-sound-applied]), [role="menuitem"]:not([data-sound-applied])'
        )
        .forEach((item) => {
          if (
            !item.hasAttribute("data-sound-applied") &&
            !item.closest("header")
          ) {
            const cleanup = attachSoundToEvent(
              item as HTMLElement,
              "mouseenter",
              SoundEffect.HOVER,
              {
                debounce: true,
                debounceTime: 50,
              }
            );
            item.setAttribute("data-sound-applied", "true");
            cleanupFunctions.current.push(cleanup);
          }
        });

      // Apply click sound to ALL clickable elements (except navbar items - they already have click sounds)
      const navbarItems = document.querySelectorAll(
        "header a, header button, nav a, nav button"
      );
      const navbarItemsArray = Array.from(navbarItems);

      document
        .querySelectorAll(
          'a, button, [role="button"], [type="submit"], [type="button"], [role="link"], [role="tab"], .clickable, [aria-pressed], [aria-selected], .cursor-pointer, [onclick], [data-clickable="true"], input[type="checkbox"], input[type="radio"], select, summary, details'
        )
        .forEach((element) => {
          // Skip navbar items to avoid double sounds
          if (
            !element.hasAttribute("data-click-sound-applied") &&
            !navbarItemsArray.includes(element) &&
            !element.closest("header")
          ) {
            const cleanup = attachSoundToEvent(
              element as HTMLElement,
              "click",
              SoundEffect.CLICK
            );
            element.setAttribute("data-click-sound-applied", "true");
            cleanupFunctions.current.push(cleanup);
          }
        });

      // Apply typing sound to all inputs and textareas
      document.querySelectorAll("input, textarea").forEach((input) => {
        const handleTypingSound = () => {
          const value = (input as HTMLInputElement | HTMLTextAreaElement).value;
          if (value.length % 4 === 0 && value.length > 0) {
            playSound(SoundEffect.TYPING, 0.1);
          }
        };

        input.addEventListener("input", handleTypingSound);
        cleanupFunctions.current.push(() => {
          input.removeEventListener("input", handleTypingSound);
        });
      });

      // Global scroll sound
      const handleScroll = () => {
        debouncedScrollSound(0.1);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      cleanupFunctions.current.push(() => {
        window.removeEventListener("scroll", handleScroll);
      });

      // Set up a mutation observer to watch for dynamically added elements
      const observeDOM = () => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.addedNodes.length) {
              // When new nodes are added, apply click sounds to any new clickable elements
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                  // ELEMENT_NODE
                  const element = node as HTMLElement;

                  // Skip elements in navbar/header
                  if (element.closest("header") || element.closest("nav")) {
                    return;
                  }

                  // Check for clickable elements
                  const clickables = element.querySelectorAll(
                    'a, button, [role="button"], [type="submit"], [type="button"], [role="link"], [role="tab"], .clickable, [aria-pressed], [aria-selected], .cursor-pointer, [onclick], [data-clickable="true"], input[type="checkbox"], input[type="radio"], select, summary, details'
                  );

                  clickables.forEach((clickable) => {
                    if (!clickable.hasAttribute("data-click-sound-applied")) {
                      const cleanup = attachSoundToEvent(
                        clickable as HTMLElement,
                        "click",
                        SoundEffect.CLICK
                      );
                      clickable.setAttribute(
                        "data-click-sound-applied",
                        "true"
                      );
                      cleanupFunctions.current.push(cleanup);
                    }
                  });

                  // Check if the element itself is clickable
                  if (
                    element.tagName === "A" ||
                    element.tagName === "BUTTON" ||
                    element.getAttribute("role") === "button" ||
                    element.getAttribute("role") === "link" ||
                    element.getAttribute("role") === "tab" ||
                    element.classList.contains("clickable") ||
                    element.classList.contains("cursor-pointer") ||
                    element.hasAttribute("onclick") ||
                    element.getAttribute("data-clickable") === "true"
                  ) {
                    if (!element.hasAttribute("data-click-sound-applied")) {
                      const cleanup = attachSoundToEvent(
                        element,
                        "click",
                        SoundEffect.CLICK
                      );
                      element.setAttribute("data-click-sound-applied", "true");
                      cleanupFunctions.current.push(cleanup);
                    }
                  }
                }
              });
            }
          });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Return cleanup function
        return () => observer.disconnect();
      };

      // Start observing for dynamically added elements
      const observerCleanup = observeDOM();
      cleanupFunctions.current.push(observerCleanup);
    }, 1000); // Wait 1 second to ensure the DOM is fully loaded

    // Cleanup function
    return () => {
      clearTimeout(timeout);
      cleanupFunctions.current.forEach((cleanup) => cleanup());
    };
  }, [attachSoundToEvent, playSound, debouncedScrollSound]);

  return <>{children}</>;
};

export default SoundProvider;
